export * from './flexibleDisplay.js';
import { getTemplateInputCount, normalizeFlexibleDisplay, validateFlexibleDisplayTemplate } from './flexibleDisplay.js';
export const STATIC_PROFILE_VERSION = 1;
export const STATIC_TEMPLATE_SCHEMA_VERSION = 2;
export const SCREEN_PROFILE_VERSION = 1;
export const MOTION_PROFILE_VERSION = 1;
export const SINGLE_SCREEN_PROFILE = {
    id: 'single-screen',
    version: SCREEN_PROFILE_VERSION
};
export const DUAL_SCREEN_PROFILE = {
    id: 'dual-screen',
    version: SCREEN_PROFILE_VERSION
};
export const TRIPLE_SCREEN_PROFILE = {
    id: 'triple-screen',
    version: SCREEN_PROFILE_VERSION
};
export function normalizeScreenProfileRef(value) {
    if (!value || typeof value !== 'object') {
        return { ...SINGLE_SCREEN_PROFILE };
    }
    const candidate = value;
    if ((candidate.id === 'single-screen' || candidate.id === 'dual-screen' || candidate.id === 'triple-screen') &&
        Number(candidate.version) === SCREEN_PROFILE_VERSION) {
        return { id: candidate.id, version: SCREEN_PROFILE_VERSION };
    }
    return { ...SINGLE_SCREEN_PROFILE };
}
export function isDualScreenProfile(value) {
    return normalizeScreenProfileRef(value).id === 'dual-screen';
}
export function isTripleScreenProfile(value) {
    return normalizeScreenProfileRef(value).id === 'triple-screen';
}
export function getScreenProfileCount(value) {
    const profile = normalizeScreenProfileRef(value);
    return profile.id === 'triple-screen' ? 3 : profile.id === 'dual-screen' ? 2 : 1;
}
export function getScreenSlots(value) {
    const screenCount = getScreenProfileCount(value);
    return [
        { id: 'screen-1', label: 'Screen 1', order: 0, zIndex: 0, defaultFit: 'cover' },
        ...(screenCount >= 2
            ? [{ id: 'screen-2', label: 'Screen 2', order: 1, zIndex: 1, defaultFit: 'cover' }]
            : []),
        ...(screenCount >= 3
            ? [{ id: 'screen-3', label: 'Screen 3', order: 2, zIndex: 2, defaultFit: 'cover' }]
            : [])
    ];
}
export function normalizeMotionProfileRef(value) {
    if (value && typeof value === 'object') {
        const candidate = value;
        if ((candidate.id === 'motion.single-screen' ||
            candidate.id === 'motion.dual-screen' ||
            candidate.id === 'motion.triple-screen' || candidate.id === 'motion.duo-inner' || candidate.id === 'motion.duo-both') &&
            Number(candidate.version) === MOTION_PROFILE_VERSION) {
            return { id: candidate.id, version: MOTION_PROFILE_VERSION };
        }
    }
    return { id: 'motion.single-screen', version: MOTION_PROFILE_VERSION };
}
export function resolveMotionAtlasAuxRole(options) {
    if (options.atlasLayout === 'extended-6') {
        return 'alternate-screen-mask';
    }
    if (options.motionDynamicIslandToggle === true) {
        return 'alternate-screen-mask';
    }
    return options.screenBlurEnabled === false ? 'ignored' : 'screen-blur-mask';
}
export function isMultiScreenTrackingDocument(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value;
    if (candidate.format !== 'mockupCreator.multiScreenSequence' ||
        candidate.version !== 2 ||
        !Number.isFinite(candidate.duration) ||
        Number(candidate.duration) <= 0 ||
        !Number.isFinite(candidate.fps) ||
        Number(candidate.fps) <= 0 ||
        !Number.isInteger(candidate.frameCount) ||
        Number(candidate.frameCount) <= 0 ||
        !Array.isArray(candidate.tracks) ||
        (candidate.tracks.length < 2 || candidate.tracks.length > 3)) {
        return false;
    }
    const ids = candidate.tracks.map((track) => track?.id);
    const expectedIds = candidate.tracks.length === 3
        ? ['screen-1', 'screen-2', 'screen-3']
        : ['screen-1', 'screen-2'];
    const structurallyValid = (expectedIds.every((id) => ids.includes(id)) &&
        new Set(ids).size === expectedIds.length &&
        candidate.tracks.every((track) => Array.isArray(track.frames) &&
            track.frames.length === candidate.frameCount &&
            track.frames.every((frame) => Number.isFinite(frame?.t) &&
                Array.isArray(frame?.points) &&
                frame.points.length === 4 &&
                frame.points.every((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y)))));
    if (!structurallyValid) {
        return false;
    }
    const firstTrack = candidate.tracks.find((track) => track.id === 'screen-1');
    const otherTracks = candidate.tracks.filter((track) => track.id !== 'screen-1');
    let previousTime = Number.NEGATIVE_INFINITY;
    for (let index = 0; index < Number(candidate.frameCount); index += 1) {
        const firstTime = firstTrack.frames[index].t;
        if (firstTime < 0 ||
            firstTime <= previousTime ||
            otherTracks.some((track) => Math.abs(firstTime - track.frames[index].t) > 0.0005)) {
            return false;
        }
        previousTime = firstTime;
    }
    return true;
}
export function getMotionPlaybackDuration(templateDuration, userVideoDuration, loopEnabled) {
    const normalizedTemplateDuration = normalizePositiveMotionDuration(templateDuration);
    const normalizedUserVideoDuration = normalizePositiveMotionDuration(userVideoDuration);
    const effectiveDuration = Math.max(normalizedTemplateDuration, normalizedUserVideoDuration);
    if (!loopEnabled || normalizedTemplateDuration <= 0 || normalizedUserVideoDuration <= normalizedTemplateDuration) {
        return effectiveDuration;
    }
    return Math.ceil(normalizedUserVideoDuration / normalizedTemplateDuration) * normalizedTemplateDuration;
}
export function getMotionTemplatePlaybackTime(templateDuration, playbackTime, loopEnabled) {
    const normalizedTemplateDuration = normalizePositiveMotionDuration(templateDuration);
    const normalizedPlaybackTime = Number.isFinite(playbackTime) ? Math.max(0, playbackTime) : 0;
    if (normalizedTemplateDuration <= 0) {
        return 0;
    }
    if (!loopEnabled) {
        return Math.min(normalizedPlaybackTime, normalizedTemplateDuration);
    }
    return normalizedPlaybackTime % normalizedTemplateDuration;
}
function normalizePositiveMotionDuration(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}
export function compareMotionTimings(left, right, options = {}) {
    const leftLabel = options.leftLabel || 'First source';
    const rightLabel = options.rightLabel || 'Second source';
    const maxFrameDelta = Math.max(0, Math.floor(options.maxFrameDelta ?? 1));
    const issues = [
        ...validateMotionTimingDescriptor(left, leftLabel),
        ...validateMotionTimingDescriptor(right, rightLabel)
    ];
    if (issues.some((issue) => issue.severity === 'error')) {
        return issues;
    }
    if (Math.abs(left.fps - right.fps) > 0.01) {
        issues.push({
            severity: 'error',
            field: 'fps',
            message: `${leftLabel}/${rightLabel} FPS mismatch: ${formatMotionMetric(left.fps)} and ${formatMotionMetric(right.fps)}.`
        });
    }
    const durationTolerance = 1 / Math.max(1, Math.min(left.fps, right.fps)) + 0.002;
    if (Math.abs(left.duration - right.duration) > durationTolerance) {
        issues.push({
            severity: 'error',
            field: 'duration',
            message: `${leftLabel}/${rightLabel} duration mismatch: ${formatMotionMetric(left.duration)}s and ${formatMotionMetric(right.duration)}s.`
        });
    }
    const frameDelta = Math.abs(left.frameCount - right.frameCount);
    if (frameDelta > maxFrameDelta) {
        issues.push({
            severity: 'error',
            field: 'frameCount',
            message: `${leftLabel}/${rightLabel} frame count mismatch: ${left.frameCount} and ${right.frameCount}.`
        });
    }
    else if (frameDelta > 0) {
        issues.push({
            severity: 'warning',
            field: 'frameCount',
            message: `${leftLabel} and ${rightLabel} differ by ${frameDelta} frame${frameDelta === 1 ? '' : 's'} (${left.frameCount}/${right.frameCount}).`
        });
    }
    return issues;
}
function validateMotionTimingDescriptor(timing, label) {
    const issues = [];
    if (!Number.isFinite(timing.duration) || timing.duration <= 0) {
        issues.push({ severity: 'error', field: 'duration', message: `${label} duration must be a positive number.` });
    }
    if (!Number.isFinite(timing.fps) || timing.fps <= 0) {
        issues.push({ severity: 'error', field: 'fps', message: `${label} FPS must be a positive number.` });
    }
    if (!Number.isInteger(timing.frameCount) || timing.frameCount <= 0) {
        issues.push({
            severity: 'error',
            field: 'frameCount',
            message: `${label} frame count must be a positive integer.`
        });
    }
    return issues;
}
function formatMotionMetric(value) {
    return Number(value.toFixed(4)).toString();
}
export const STATIC_PROFILE_IDS = [
    'static.standard',
    'static.surface-reflection',
    'static.screen-blur',
    'static.dual-screen',
    'static.triple-screen',
    'static.duo-inner',
    'static.duo-both'
];
const DISABLED_CAPABILITIES = {
    screenCount: 1,
    diffuseColor: false,
    screenOverlayOpacity: false,
    dynamicIsland: false,
    backgroundTreatment: false,
    surfaceReflection: false,
    surfaceReflectionBlur: false,
    screenBlur: false
};
const BASE_DEFAULTS = {
    screenOverlayOpacity: 50,
    surfaceReflectionOpacity: 50,
    surfaceReflectionBlur: 50
};
const STATIC_PROFILE_REGISTRY = {
    'static.standard': {
        id: 'static.standard',
        version: STATIC_PROFILE_VERSION,
        label: 'Standard',
        description: 'Static screen mockup without a reflected screen on a surface.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay'],
        optionalAssets: ['screenMask2', 'backgroundMask'],
        requiredGeometry: ['screenQuad'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            diffuseColor: true,
            screenOverlayOpacity: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.surface-reflection': {
        id: 'static.surface-reflection',
        version: STATIC_PROFILE_VERSION,
        label: 'Blur Reflection',
        description: 'Static screen mockup with a reflected copy on a floor or surface.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay'],
        optionalAssets: ['screenMask2', 'backgroundMask', 'reflectionMask', 'reflectionBlurMask'],
        requiredGeometry: ['screenQuad', 'reflectionQuad'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            diffuseColor: true,
            screenOverlayOpacity: true,
            surfaceReflection: true,
            surfaceReflectionBlur: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.screen-blur': {
        id: 'static.screen-blur',
        version: STATIC_PROFILE_VERSION,
        label: 'Blur Screen',
        description: 'Static screen mockup with mask-controlled blur applied only to inserted screen content.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay', 'screenBlurMask'],
        optionalAssets: ['screenMask2', 'backgroundMask'],
        requiredGeometry: ['screenQuad'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            diffuseColor: true,
            screenOverlayOpacity: true,
            screenBlur: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.dual-screen': {
        id: 'static.dual-screen',
        version: STATIC_PROFILE_VERSION,
        label: 'Dual Screen',
        description: 'Static mockup with two independent screen inputs and placements.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenMask2', 'screenOverlay'],
        optionalAssets: ['backgroundMask', 'screenBlurMask'],
        requiredGeometry: ['screenQuad', 'screenQuad2'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            screenCount: 2,
            diffuseColor: true,
            screenOverlayOpacity: true,
            screenBlur: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.duo-inner': {
        id: 'static.duo-inner', version: STATIC_PROFILE_VERSION, label: 'Duo · Inner display',
        description: 'One continuous source across two projective panels and a tangent-continuous hinge.', enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay'],
        optionalAssets: ['backgroundMask', 'screenBlurMask'], requiredGeometry: ['screenQuad', 'screenQuad2'],
        capabilities: { ...DISABLED_CAPABILITIES, screenCount: 1, diffuseColor: true, screenOverlayOpacity: true, screenBlur: true },
        defaults: BASE_DEFAULTS
    },
    'static.duo-both': {
        id: 'static.duo-both', version: STATIC_PROFILE_VERSION, label: 'Duo · Outer + inner displays',
        description: 'Independent outer and inner sources; the inner source spans both panels and the hinge.', enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenMask2', 'screenOverlay'],
        optionalAssets: ['backgroundMask', 'screenBlurMask'], requiredGeometry: ['screenQuad', 'screenQuad2', 'screenQuad3'],
        capabilities: { ...DISABLED_CAPABILITIES, screenCount: 2, diffuseColor: true, screenOverlayOpacity: true, screenBlur: true },
        defaults: BASE_DEFAULTS
    },
    'static.triple-screen': {
        id: 'static.triple-screen',
        version: STATIC_PROFILE_VERSION,
        label: 'Triple Screen',
        description: 'Static mockup with three independent screen inputs and placements.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenMask2', 'screenMask3', 'screenOverlay'],
        optionalAssets: ['backgroundMask', 'screenBlurMask'],
        requiredGeometry: ['screenQuad', 'screenQuad2', 'screenQuad3'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            screenCount: 3,
            diffuseColor: true,
            screenOverlayOpacity: true,
            screenBlur: true
        },
        defaults: BASE_DEFAULTS
    }
};
export function isMotionTemplateType(value) {
    const normalized = String(value || '').trim().toUpperCase().replace(/_/g, '-');
    return normalized === 'MOTION' || normalized === 'MOTION-CG' || normalized === 'MOTION-AI';
}
export function isStaticProfileId(value) {
    return typeof value === 'string' && STATIC_PROFILE_IDS.includes(value);
}
export function normalizeStaticProfileRef(value) {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const candidate = value;
    if (!isStaticProfileId(candidate.id) || Number(candidate.version) !== STATIC_PROFILE_VERSION) {
        return null;
    }
    return {
        id: candidate.id,
        version: STATIC_PROFILE_VERSION
    };
}
export function getStaticProfileDefinition(profile) {
    const id = typeof profile === 'string' ? profile : profile.id;
    return STATIC_PROFILE_REGISTRY[id];
}
export function listStaticProfileDefinitions(options = {}) {
    return STATIC_PROFILE_IDS
        .map((id) => STATIC_PROFILE_REGISTRY[id])
        .filter((profile) => options.includeDisabled === true || profile.enabled);
}
export function inferStaticProfile(template) {
    if (template.isVideo === true || isMotionTemplateType(template.templateType)) {
        return null;
    }
    const explicitProfile = normalizeStaticProfileRef(template.staticProfile);
    if (explicitProfile) {
        return explicitProfile;
    }
    if (hasAsset(template.screenBlurMask)) {
        return { id: 'static.screen-blur', version: STATIC_PROFILE_VERSION };
    }
    if (template.reflectionEnabled === true && isQuad(template.reflectionQuad)) {
        return { id: 'static.surface-reflection', version: STATIC_PROFILE_VERSION };
    }
    return { id: 'static.standard', version: STATIC_PROFILE_VERSION };
}
export function resolveStaticTemplateCapabilities(template) {
    const profile = inferStaticProfile(template);
    if (!profile) {
        return { ...DISABLED_CAPABILITIES };
    }
    const definition = getStaticProfileDefinition(profile);
    return {
        ...definition.capabilities,
        screenCount: definition.capabilities.screenCount,
        screenOverlayOpacity: definition.capabilities.screenOverlayOpacity &&
            hasAsset(template.overlay) &&
            !hasAsset(template.reflectionBlurMask),
        dynamicIsland: !template.flexibleDisplay && definition.capabilities.screenCount === 1 && hasAsset(template.mask2),
        backgroundTreatment: hasAsset(template.backgroundMask),
        surfaceReflection: definition.capabilities.surfaceReflection && template.reflectionEnabled === true && isQuad(template.reflectionQuad),
        surfaceReflectionBlur: definition.capabilities.surfaceReflectionBlur && template.reflectionEnabled === true && isQuad(template.reflectionQuad),
        screenBlur: definition.capabilities.screenBlur && hasAsset(template.screenBlurMask)
    };
}
export function validateStaticProfileTemplate(template) {
    const profile = inferStaticProfile(template);
    if (!profile) {
        return [];
    }
    const definition = getStaticProfileDefinition(profile);
    const issues = [];
    for (const message of validateFlexibleDisplayTemplate(template))
        issues.push({ field: 'staticProfile', message });
    if (normalizeFlexibleDisplay(template.flexibleDisplay)) {
        if (getTemplateInputCount(template) === 1 && hasAsset(template.mask2)) {
            issues.push({ field: 'screenMask2', message: 'The selected Duo display uses one whole-display mask, not a mask per tracking guide.' });
        }
        if (hasAsset(template.mask3)) {
            issues.push({ field: 'screenMask3', message: 'Duo has at most two display masks. Tracking 2 and 3 share the inner-display mask.' });
        }
    }
    if (!definition.enabled) {
        issues.push({ field: 'staticProfile', message: `${definition.label} is not enabled yet.` });
    }
    if (profile.id === 'static.surface-reflection' && template.reflectionEnabled !== true) {
        issues.push({ field: 'staticProfile', message: 'Blur Reflection requires surface reflection to be enabled.' });
    }
    if (profile.id !== 'static.surface-reflection' && template.reflectionEnabled === true) {
        issues.push({ field: 'staticProfile', message: `${definition.label} does not support surface reflection.` });
    }
    const expectedScreenCount = profile.id === 'static.triple-screen' || profile.id === 'static.duo-both' ? 3
        : profile.id === 'static.dual-screen' || profile.id === 'static.duo-inner' ? 2 : 1;
    if (getScreenProfileCount(template.screenProfile) !== expectedScreenCount) {
        issues.push({
            field: 'staticProfile',
            message: expectedScreenCount === 1
                ? `${definition.label} cannot use a multi-screen screen profile.`
                : `${definition.label} requires the ${expectedScreenCount === 3 ? 'triple-screen' : 'dual-screen'} screen profile.`
        });
    }
    for (const slot of definition.requiredAssets) {
        if (!hasAsset(readAssetSlot(template, slot))) {
            issues.push({ field: slot, message: `${slot} is required for ${definition.label}.` });
        }
    }
    for (const slot of definition.requiredGeometry) {
        if (!isQuad(template[slot])) {
            issues.push({ field: slot, message: `${slot} must contain four finite points.` });
        }
    }
    return issues;
}
function readAssetSlot(template, slot) {
    switch (slot) {
        case 'preview':
            return template.preview;
        case 'diffuse':
            return template.base;
        case 'screenMask':
            return template.mask;
        case 'screenMask2':
            return template.mask2;
        case 'screenMask3':
            return template.mask3;
        case 'screenOverlay':
            return template.overlay;
        case 'backgroundMask':
            return template.backgroundMask;
        case 'reflectionMask':
            return template.reflectionMask;
        case 'reflectionBlurMask':
            return template.reflectionBlurMask;
        case 'screenBlurMask':
            return template.screenBlurMask;
    }
}
function hasAsset(value) {
    return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
}
function isQuad(value) {
    return (Array.isArray(value) &&
        value.length === 4 &&
        value.every((point) => {
            if (!point || typeof point !== 'object') {
                return false;
            }
            const candidate = point;
            return Number.isFinite(Number(candidate.x)) && Number.isFinite(Number(candidate.y));
        }));
}
/* ------------------------------------------------------------------ *
 * Motion Puzzle Matte — independent recolouring of up to three regions
 *
 * A separate synchronous MP4 whose pure red, green and blue channels mark
 * three regions of the mockup. The atlas is deliberately untouched: its aux
 * tile is already spent on the blur mask or the alternate screen mask, so a
 * fourth role there would collide with existing templates.
 *
 * The channel value doubles as coverage. Where compression or motion blur
 * bleeds one channel into another both values fall, so the effect fades out
 * at the edge instead of needing a hard classification step.
 * ------------------------------------------------------------------ */
export const MOTION_COLOR_MATTE_VERSION = 1;
/** Order here is the order the user sees: Color 1, Color 2, Color 3. */
export const MOTION_COLOR_MATTE_CHANNELS = ['r', 'g', 'b'];
export function isMotionColorMatteChannel(value) {
    return typeof value === 'string' && MOTION_COLOR_MATTE_CHANNELS.includes(value);
}
/**
 * Returns null for anything a template published before this feature could
 * carry, so an older catalog entry stays byte-identical after a round trip.
 */
export function normalizeMotionColorMatte(value) {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const candidate = value;
    if (Number(candidate.version) !== MOTION_COLOR_MATTE_VERSION) {
        return null;
    }
    const rawChannels = candidate.channels;
    if (!Array.isArray(rawChannels)) {
        return null;
    }
    // Keep the canonical r/g/b order regardless of how the entry was written,
    // and drop duplicates — the user-facing numbering has to be stable.
    const channels = MOTION_COLOR_MATTE_CHANNELS.filter((channel) => rawChannels.some((entry) => isMotionColorMatteChannel(entry) && entry === channel));
    if (channels.length === 0) {
        return null;
    }
    return { version: MOTION_COLOR_MATTE_VERSION, channels: [...channels] };
}
/** Label shown above each control: Color 1 … Color 3, in channel order. */
export function getMotionColorMatteLabel(matte, channel) {
    const index = matte.channels.indexOf(channel);
    return index < 0 ? '' : `Color ${index + 1}`;
}
